/**
 * W5b — sold-comps DISPLAY helpers (pure). The confidence tier + the "why" caveats shown next to the
 * eBay sold-velocity / sold-price on the product surface. DISPLAY-ONLY — this drives no engine math
 * (Correction 1). Pure (no I/O) so it is unit-tested directly.
 *
 * The per-distributor velocity confidence FACTOR is resolved at display time through the existing
 * generic resolve_param_numeric (the component calls the RPC; this module only RESERVES the key) —
 * resolve_param_numeric is key-agnostic, so reserving a key requires ZERO function change. Consumed
 * AT DISPLAY ONLY this build; the engine consumption (velocity × factor in forecast_restock, with the
 * price haircut) is the deferred drives-margin piece.
 */

// Reserved param key — the per-distributor sold-velocity confidence factor (6-level resolvable via
// resolve_param_numeric, no fn change). Default/identity is deferred to the consuming piece.
export const SOLD_VELOCITY_CONFIDENCE_PARAM_KEY = 'ebay_sold_velocity_confidence';

export interface SoldCompsDisplayInput {
  n_transactions: number | null;
  units_monthly: number | null;
  window_days: number | null;
  median_unit_price: number | null;
  median_landed_price: number | null;
  price_mix: {
    auction?: number;
    bin_exact?: number;
    bin_ceiling?: number;
    qty_defaulted?: number;
    price_included?: number;
  } | null;
}

export type ConfidenceTier = 'none' | 'low' | 'medium';

/**
 * Confidence tier for eBay sold-comp data — DELIBERATELY conservative. eBay sold-comps are recall-
 * limited (a sold-comps API never returns the full sold set) and window-bounded, so the tier tops
 * out at 'medium' (never 'high'): the lower-tier framing the spine calls for. Keys on transaction
 * count — more transactions ⇒ more trustworthy velocity. The per-distributor confidence factor
 * tempers further at display (resolved separately).
 */
export function soldCompsConfidenceTier(input: SoldCompsDisplayInput): ConfidenceTier {
  const n = input.n_transactions;
  if (n === null || n <= 0) return 'none';
  if (n < 10) return 'low';
  return 'medium'; // capped — eBay sold-velocity is never 'high' (recall-limited)
}

/**
 * The caveats to surface next to the sold-velocity / sold-price (the "why" the tier is what it is):
 * the window, the recall-limited transaction count, and — when present — that raw best-offer ceiling
 * rows are still in the price stats (the Correction-2 deferred exclusion) and that qty-defaulted rows
 * were dropped from price. Empty when there is no sold data.
 */
export function soldCompsWhy(input: SoldCompsDisplayInput): string[] {
  const n = input.n_transactions;
  if (n === null || n <= 0) return [];
  const why: string[] = [];
  const w = input.window_days ?? 90;
  why.push(`${w}-day sold window`);
  why.push(`${n} transaction${n === 1 ? '' : 's'} (recall-limited — not the full sold set)`);
  const ceil = input.price_mix?.bin_ceiling ?? 0;
  if (ceil > 0)
    why.push(`${ceil} best-offer ceiling row${ceil === 1 ? '' : 's'} still in price stats (raw)`);
  const dropped = input.price_mix?.qty_defaulted ?? 0;
  if (dropped > 0)
    why.push(`${dropped} unknown-pack row${dropped === 1 ? '' : 's'} excluded from price`);
  return why;
}

const TIER_LABEL: Record<ConfidenceTier, string> = {
  none: 'No sold data',
  low: 'Low confidence',
  medium: 'Moderate confidence',
};
export function soldCompsTierLabel(tier: ConfidenceTier): string {
  return TIER_LABEL[tier];
}
